<template>
  <v-stepper-vertical-item
    :value="3"
    :elevation="0"
    :class="{
      'step-completed': currentStep > 3,
    }"
  >
    <template #title>
      <div class="subtitle1">{{ $t("modals.allowCloudService.step3.title") }}</div>
    </template>
    <template #icon>
      <v-icon v-if="currentStep > 3" icon="$check" />
      <span v-else>4</span>
    </template>
    <p
      class="body2 mb-4"
      v-html="$t('modals.allowCloudService.step3.description', { service: cloudEmailServiceName })"
    />
    <div>
      <span class="subtitle2">{{ $t("modals.allowCloudService.step3.resendLabel") }}</span>
      <span class="ml-2 coro-link body2" @click="sendTestEmails">{{
        $t("modals.allowCloudService.step3.sendAgain")
      }}</span>
    </div>
    <div class="mt-5 d-flex">
      <v-btn color="primary" rounded min-width="120" :loading="loading" @click="moveToNextStep">
        {{ $t("modals.allowCloudService.step3.actionBtn") }}
      </v-btn>
      <v-btn class="ml-2" variant="text" color="primary" @click="goBack">
        {{ $t("general.back") }}
      </v-btn>
    </div>
  </v-stepper-vertical-item>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref } from "vue";
import { Service } from "@/constants/cloud-apps.ts";
import {
  SatConfigurationStatus,
  SatServiceProvider,
  SatServiceProviderToServiceMap,
} from "@/constants/security-awareness.ts";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import { useI18n } from "vue-i18n";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";
import { serviceToEmailProviderMap } from "@/constants/email-proxy.ts";
import { EmailAddInsProvider } from "@/constants/emails.ts";

export default defineComponent({
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Object as PropType<SatConnectionConfigForm>,
      required: true,
    },
  },
  emits: ["next", "back"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const securityAwarenessStore = useSecurityAwarenessModule();
    const loading = ref(false);

    const modelValue = computed(() => props.modelValue);

    const isOtherProvider = computed(() => {
      return modelValue.value.selectedService === "other";
    });

    const cloudEmailServiceName = computed(() => {
      if (isOtherProvider.value) {
        return modelValue.value.domain;
      }

      const emailProvider: EmailAddInsProvider =
        serviceToEmailProviderMap[
          modelValue.value.selectedService as Service.OFFICE_365 | Service.G_SUITE
        ];

      return i18n.t(`general.${emailProvider}`);
    });

    const satConfigurationProvider = computed(
      () =>
        SatServiceProviderToServiceMap[modelValue.value.selectedService as Service] ??
        SatServiceProvider.CUSTOM
    );

    const sendTestEmails = async () => {
      loading.value = true;
      try {
        await securityAwarenessStore.sendTestEmails(satConfigurationProvider.value);
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    const moveToNextStep = async () => {
      loading.value = true;
      await securityAwarenessStore.updateSatConfig(
        {
          status: SatConfigurationStatus.EMAIL_CONFIRMED,
        },
        satConfigurationProvider.value
      );
      loading.value = false;
      emit("next");
    };

    const goBack = () => {
      emit("back");
    };

    return {
      cloudEmailServiceName,
      sendTestEmails,
      moveToNextStep,
      loading,
      goBack,
    };
  },
});
</script>
